import { usePostHog } from '@posthog/react';
import * as Sentry from '@sentry/browser';
import { UserTypes } from 'common';
import { BookingWizardFlowTypes } from 'common/dist/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { useInjection } from 'inversify-react';
import { useEffect } from 'react';

import { AsyncHookResult, InjectableHook } from 'domain/hooks';
import { BookingForm } from 'infrastructure/redux/slices/bookingWizard.slice';

export enum UserVerificationError {
  Unknown = 'unknown',
  Unauthorized = 'unauthorized',
  ValidationError = 'email_validation_error',
}

export interface LoginWithVerificationTokenOutputBoundary {
  accessToken: string;
  redirectTo: string;
  refreshToken: string;
  email: string;
  deletedAt?: Date;
}

export const VerifyUserAdapter = Symbol('VerifyUserAdapter');

export type IVerifyUserAdapter = InjectableHook<
  AsyncHookResult<LoginWithVerificationTokenOutputBoundary, UserVerificationError> & {
    verifyUser: (
      token: string | null,
      email: string | null,
      bookingData?: string | null,
      useMobile?: boolean,
    ) => void;
    handleAccessToken: () => void;
    redirectToCheckYourEmail: (error: UserVerificationError) => void;
    redirectToConfirmReservation: () => void;
    handleSaveUserDataToStore: (userSummary: UserTypes.UserSummary | undefined) => void;
    handleGetUser: () => Promise<UserTypes.UserSummary> | undefined;
    isUserDataLoading: boolean;
    isAlreadyLoggedIn: boolean;
    redirectToPaymentDetails: () => void;
    redirectToSelectExperience: () => void;
    redirectToLocationSelect: () => void;
    redirectToChooseLessonTime: () => void;
    redirectToSelectFittingStartTime: () => void;
    logoutSignedInUser: () => void;
    bookingFormData: BookingForm;
    experienceType?: BookingWizardFlowTypes;
  }
>;

const useCaseVerifyUser = () => {
  const posthog = usePostHog();
  const adapter = useInjection<IVerifyUserAdapter>(VerifyUserAdapter);
  const {
    error,
    result,
    inProgress,
    verifyUser,
    handleAccessToken,
    redirectToCheckYourEmail,
    redirectToConfirmReservation,
    handleSaveUserDataToStore,
    redirectToPaymentDetails,
    redirectToSelectExperience,
    redirectToChooseLessonTime,
    redirectToSelectFittingStartTime,
    handleGetUser,
    isAlreadyLoggedIn,
    logoutSignedInUser,
    bookingFormData,
    experienceType,
  } = adapter();

  const handleVerifyUser = (
    token: string | null,
    email: string | null,
    bookingUUID: string | null,
    useMobile?: boolean,
  ) => {
    if (isAlreadyLoggedIn) {
      logoutSignedInUser();
      verifyUser(token, email, bookingUUID, useMobile);
    } else {
      verifyUser(token, email, bookingUUID, useMobile);
    }
  };

  useEffect(() => {
    if (!result && error) {
      redirectToCheckYourEmail(error);
    }
    if (result?.accessToken) {
      handleAccessToken();
      const fetchUserData = async () => {
        const payload = await handleGetUser();
        if (!payload?.email) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Verify user confirm' + JSON.stringify(payload),
            level: 'error',
          });
        }

        handleSaveUserDataToStore(payload);

        if (payload?.id) {
          posthog?.identify(payload.id, {
            email: payload.email,
            first_name: payload.firstName,
            last_name: payload.lastName,
            role: payload.role,
            preferred_location:
              payload.preferredLocation && 'name' in payload.preferredLocation
                ? payload.preferredLocation.name
                : undefined,
          });
          if (payload.email) {
            posthog?.capture('$merge_dangerously', {
              alias: payload.email,
              distinct_id: payload.id,
            });
          }
        }

        if (!payload?.creditCard) {
          redirectToPaymentDetails();
          return;
        }
        if (bookingFormData?.appointments && bookingFormData.appointments.length > 0) {
          redirectToConfirmReservation();
          return;
        }
        switch (experienceType) {
          case BookingWizardFlowTypes.Lesson:
          case BookingWizardFlowTypes.LessonByCoach:
          case BookingWizardFlowTypes.LessonByDate:
          case BookingWizardFlowTypes.FirstLesson:
          case BookingWizardFlowTypes.ClubFittingbyCoach:
            redirectToChooseLessonTime();
            return;
          case BookingWizardFlowTypes.ClubFitting:
            redirectToSelectFittingStartTime();
            return;
          default:
            redirectToSelectExperience();
        }
      };
      fetchUserData();
    }
  }, [result, error, bookingFormData]);

  return {
    inProgress,
    error,
    result,
    verifyUser: handleVerifyUser,
  };
};

export default useCaseVerifyUser;
